import scryptModule from 'scrypt-js';
import firebase from '../config/firebase';
import { SUBMISSION_PUBLISH_STATUSES } from '../config/constants';
import { normalizePipelineStatus, pipelineRequiresApprovalPin, getPipelineStepLabel } from './submissions';
import apiClient from './api';

function normalizeAdminPin(pin) {
  return String(pin ?? "").trim();
}

function assertValidAdminPin(pin) {
  const normalized = normalizeAdminPin(pin);
  if (!/^\d{4,6}$/.test(normalized)) {
    throw new Error("PIN must be 4–6 digits.");
  }
  return normalized;
}

function getCoownFirestore() {
  return firebase.firestore();
}

async function scryptDeriveHex(pin, saltHex) {
  if (!scryptModule?.scrypt) {
    throw new Error("PIN module failed to load. Hard-refresh the page (Ctrl+Shift+R).");
  }
  const password = new TextEncoder().encode(String(pin));
  const saltBytes = new TextEncoder().encode(saltHex);
  const key = await scryptModule.scrypt(password, saltBytes, 16384, 8, 1, 32);
  return Array.from(key).map((b) => b.toString(16).padStart(2, "0")).join("");
}

async function scryptHashPin(pin) {
  const salt = Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  const derived = await scryptDeriveHex(pin, salt);
  return `${salt}:${derived}`;
}

async function scryptVerifyPin(pin, stored) {
  if (!stored || !/^\d{4,6}$/.test(String(pin || ""))) return false;
  const [salt, expected] = String(stored).split(":");
  if (!salt || !expected) return false;
  const derived = await scryptDeriveHex(pin, salt);
  return derived === expected;
}

async function adminFirestoreGetPinHash() {
  const uid = firebase.auth().currentUser?.uid;
  if (!uid) return null;
  const snap = await getCoownFirestore().collection("users").doc(uid).get();
  return snap.exists ? snap.data()?.adminPinHash || null : null;
}

async function adminFirestoreSavePin(pin) {
  if (!firebase.auth().currentUser) throw new Error("Sign in required.");
  const hash = await scryptHashPin(pin);
  await getCoownFirestore().collection("users").doc(firebase.auth().currentUser.uid).set(
    { adminPinHash: hash, updatedAt: firebase.firestore.FieldValue.serverTimestamp() },
    { merge: true }
  );
}

async function adminFirestoreUpdateSubmission(id, { status, notes }) {
  if (!firebase.auth().currentUser) throw new Error("Sign in required.");
  await getCoownFirestore().collection("property_submissions").doc(id).update({
    status,
    notes: notes || "",
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    pipelineDate: new Date().toISOString().split("T")[0]
  });
}

async function adminPublishSubmissionListing(submissionId) {
  const res = await apiClient.publishSubmission(submissionId);
  return res?.data;
}

async function adminUnpublishSubmissionListing(submissionId) {
  const res = await apiClient.unpublishSubmission(submissionId);
  return res?.data;
}

async function adminPipelineViaFirestore(id, status, adminPin, fromStatus) {
  const label = getPipelineStepLabel(status);
  const notes = `Pipeline: ${label} (updated by admin)`;
  const normalizedPin = adminPin != null ? assertValidAdminPin(adminPin) : null;
  if (pipelineRequiresApprovalPin(fromStatus, status)) {
    if (!normalizedPin) throw new Error("Approval PIN required.");
    const existing = await adminFirestoreGetPinHash();
    if (!existing) {
      throw new Error("Set your approval PIN first (admin banner → Set up PIN).");
    }
    const ok = await scryptVerifyPin(normalizedPin, existing);
    if (!ok) throw new Error("Incorrect approval PIN.");
  }
  await adminFirestoreUpdateSubmission(id, { status, notes });
  if (SUBMISSION_PUBLISH_STATUSES.includes(status)) {
    await adminPublishSubmissionListing(id);
  } else {
    try {
      await adminUnpublishSubmissionListing(id);
    } catch (err) {
      console.warn("[admin] unpublish after Firestore pipeline failed", err);
    }
  }
}

export {
  normalizeAdminPin,
  assertValidAdminPin,
  getCoownFirestore,
  scryptDeriveHex,
  scryptHashPin,
  scryptVerifyPin,
  adminFirestoreGetPinHash,
  adminFirestoreSavePin,
  adminFirestoreUpdateSubmission,
  adminPublishSubmissionListing,
  adminUnpublishSubmissionListing,
  adminPipelineViaFirestore,
};
